import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Cluster,
  Input,
  PageHeading,
  Stack,
} from "~/components";
import { WorkflowBuilderHelp } from "~/components/workflow/WorkflowBuilderHelp";
import { WorkflowGraphEditor } from "~/components/workflow/WorkflowGraphEditor";
import { WorkflowListEditor } from "~/components/workflow/WorkflowListEditor";
import { formatValidationError, isValidationError } from "~/lib/cases";
import { HalError } from "~/lib/hal";
import { queryClient } from "~/lib/queryClient";
import { hasChildlessModule, isStandardShape } from "~/lib/workflow-graph";
import {
  useDeleteWorkflow,
  useUpdateWorkflowDefinition,
  useUpdateWorkflowName,
  workflowQueryKey,
  workflowQueryOptions,
  type GraphNode,
  type Workflow,
  type WorkflowCaseRef,
} from "~/lib/workflows";

import * as css from "./_app.workflows.$id.edit.css";

export const Route = createFileRoute("/_app/workflows/$id/edit")({
  component: WorkflowEditorPage,
  loader: ({ params }) =>
    queryClient.ensureQueryData(workflowQueryOptions(params.id)),
});

function WorkflowEditorPage() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error } = useQuery(workflowQueryOptions(id));

  if (isPending) {
    return (
      <Stack gap="lg">
        <PageHeading title="Loading workflow…" />
        <Card>
          <p>Loading workflow…</p>
        </Card>
      </Stack>
    );
  }

  if (isError) {
    return <WorkflowEditorError error={error} />;
  }

  return <WorkflowEditor workflow={data} />;
}

function WorkflowEditorError({ error }: { error: Error }) {
  const router = useRouter();
  const isNotFound = error instanceof HalError && error.status === 404;
  return (
    <Stack gap="lg">
      <PageHeading
        title={isNotFound ? "Workflow not found" : "Couldn't load workflow"}
      />
      <Card accent="danger">
        <Stack gap="md">
          <p>{error.message}</p>
          <Cluster gap="sm">
            <Button
              type="button"
              variant="secondary"
              onClick={() => router.history.back()}
            >
              Back
            </Button>
            <Link to="/workflows">
              <Button type="button" variant="secondary">
                Back to workflows
              </Button>
            </Link>
          </Cluster>
        </Stack>
      </Card>
    </Stack>
  );
}

type ViewMode = "graph" | "list";

function WorkflowEditor({ workflow }: { workflow: Workflow }) {
  const updateMutation = useUpdateWorkflowDefinition();

  const [definition, setDefinition] = useState<GraphNode>(workflow.definition);
  const [showHelp, setShowHelp] = useState(false);
  const [mode, setMode] = useState<ViewMode>("graph");

  // The list editor can only represent the "standard shape" (root module
  // → phases → tasks, triggers between phases). For anything more exotic
  // the toggle is disabled and we snap back to graph mode.
  const canUseList = isStandardShape(definition);
  useEffect(() => {
    if (!canUseList && mode === "list") setMode("graph");
  }, [canUseList, mode]);

  // When the server copy updates (e.g. after a successful save or a refetch),
  // pull the new definition in — unless we have newer local edits pending.
  const serverDefRef = useRef(workflow.definition);
  useEffect(() => {
    if (workflow.definition !== serverDefRef.current) {
      serverDefRef.current = workflow.definition;
      setDefinition(workflow.definition);
    }
  }, [workflow.definition]);

  // Debounced autosave. The editor calls setDefinition on every change;
  // this effect pushes those changes to the server 300ms after the user
  // stops editing, with optimistic cache update + rollback on failure.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const definitionRef = useRef(definition);
  definitionRef.current = definition;
  const savedRef = useRef(definition);
  useEffect(() => {
    if (definition === savedRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const next = definitionRef.current;
      // A definition with a childless module is rejected by the backend, and
      // the rollback would wipe the draft (see hasChildlessModule). Skip the
      // save without marking it saved, so the next edit that makes the
      // definition valid autosaves normally.
      if (hasChildlessModule(next)) return;
      savedRef.current = next;
      const previous = queryClient.getQueryData<Workflow>(workflowQueryKey(workflow.id));
      queryClient.setQueryData<Workflow>(workflowQueryKey(workflow.id), {
        ...workflow,
        definition: next,
      });
      updateMutation.mutate(
        { id: workflow.id, definition: next },
        {
          onError: () => {
            if (previous !== undefined) {
              queryClient.setQueryData(workflowQueryKey(workflow.id), previous);
            }
          },
        },
      );
    }, 300);
    return () => clearTimeout(saveTimerRef.current);
  }, [definition, updateMutation, workflow]);

  const handleChange = useCallback((next: GraphNode) => {
    setDefinition(next);
  }, []);

  // The childless-module state comes FIRST: while the definition is invalid
  // the autosave effect deliberately doesn't PUT (see the guard above), so
  // every mutation-derived status below describes a PREVIOUS save — showing
  // "Saved" over unsaved-and-unsavable local state was a silent-divergence
  // trap (verifier finding on the autosave guard).
  const saveStatusText = hasChildlessModule(definition)
    ? "Unsaved — empty phase needs a task"
    : updateMutation.isPending
      ? "Saving…"
      : updateMutation.isError
        ? "Save failed"
        : updateMutation.isSuccess
          ? "Saved"
          : "";

  return (
    <Stack gap="lg">
      <PageHeading
        title={workflow.name}
        subtitle={
          <>
            Workflow ID <code className={css.workflowIdCode}>{workflow.id}</code>
          </>
        }
        actions={
          <>
            <div className={css.saveStatus} aria-live="polite">
              {saveStatusText}
            </div>
            <div className={css.modeToggle} role="group" aria-label="View mode">
              <button
                type="button"
                className={
                  mode === "graph"
                    ? `${css.modeToggleBtn} ${css.modeToggleBtnActive}`
                    : css.modeToggleBtn
                }
                onClick={() => setMode("graph")}
                aria-pressed={mode === "graph"}
              >
                Graph
              </button>
              <button
                type="button"
                className={
                  mode === "list"
                    ? `${css.modeToggleBtn} ${css.modeToggleBtnActive}`
                    : css.modeToggleBtn
                }
                onClick={() => setMode("list")}
                aria-pressed={mode === "list"}
                disabled={!canUseList}
                title={
                  canUseList
                    ? undefined
                    : "List view requires the standard shape (phases → tasks, triggers between phases)."
                }
              >
                List
              </button>
            </div>
            {!canUseList && (
              <span className={css.modeNotice}>
                List unavailable — non-standard shape
              </span>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowHelp((s) => !s)}
              aria-pressed={showHelp}
            >
              {showHelp ? "Hide help" : "Help"}
            </Button>
            <Link to="/workflows">
              <Button type="button" variant="secondary">
                Back to workflows
              </Button>
            </Link>
          </>
        }
      />

      <WorkflowNameForm workflow={workflow} />

      <WorkflowDangerZone workflow={workflow} />

      <div className={css.workspace}>
        <div className={css.editorArea}>
          {mode === "graph" ? (
            <WorkflowGraphEditor definition={definition} onChange={handleChange} />
          ) : (
            <WorkflowListEditor definition={definition} onChange={handleChange} />
          )}
        </div>
        {showHelp && <WorkflowBuilderHelp mode={mode} />}
      </div>
    </Stack>
  );
}

// ── Name form ──────────────────────────────────────────────────────────
//
// Renaming is an explicit-save form rather than the autosave pattern
// the graph editor uses. A workflow's name is its primary identifier
// to the firm; the gravity of changing it warrants a click rather
// than a debounced keystroke.

function WorkflowNameForm({ workflow }: { workflow: Workflow }) {
  const updateMutation = useUpdateWorkflowName();
  const [name, setName] = useState(workflow.name);

  // Re-sync the input when the server copy changes (e.g. a refetch
  // after the save) but only when there's no pending local edit.
  const serverNameRef = useRef(workflow.name);
  useEffect(() => {
    if (workflow.name !== serverNameRef.current) {
      serverNameRef.current = workflow.name;
      setName(workflow.name);
    }
  }, [workflow.name]);

  const trimmed = name.trim();
  const nameChanged = trimmed !== workflow.name && trimmed.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameChanged) return;
    updateMutation.mutate({ id: workflow.id, name: trimmed });
  };

  const mutationError = updateMutation.error;
  const mutationErrorIsValidation = isValidationError(mutationError);

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Stack gap="xs">
            <label htmlFor="workflow-name">
              <strong>Workflow name</strong>
            </label>
            <Input
              id="workflow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Stack>
          <Cluster gap="sm">
            <Button
              type="submit"
              variant="primary"
              disabled={updateMutation.isPending || !nameChanged}
            >
              {updateMutation.isPending ? "Saving…" : "Save name"}
            </Button>
            {updateMutation.isSuccess && !nameChanged && (
              <span className={css.saveStatus} aria-live="polite">
                Saved
              </span>
            )}
          </Cluster>
          {mutationError !== null && (
            <Alert variant="danger" icon={<AlertTriangle size={18} />}>
              {mutationErrorIsValidation
                ? formatValidationError(mutationError)
                : "Something went wrong saving the workflow name. Please try again."}
            </Alert>
          )}
        </Stack>
      </form>
    </Card>
  );
}

// ── Danger zone ────────────────────────────────────────────────────────
//
// Deleting a workflow is rejected by the API (TMF-118) when any
// non-soft-deleted Case still references it. We mirror that check on
// the frontend by inspecting workflow._embedded?.cases (populated by
// goat's HandleView preload) — if any are present, disable the
// delete button and surface the case list so the operator can click
// through and close them out first.

function WorkflowDangerZone({ workflow }: { workflow: Workflow }) {
  const navigate = useNavigate();
  const deleteMutation = useDeleteWorkflow();
  // The API embeds projects (the workflow_id FK lives on the project
  // supertype now, TMF-170); derive the case link list from each project's
  // nested case, which is what carries the name. Projects without an embedded
  // case are skipped.
  const cases: WorkflowCaseRef[] = (workflow._embedded?.projects ?? [])
    .map((p) => ({ id: p._embedded?.case?.id ?? "", name: p._embedded?.case?.name ?? "" }))
    .filter((c) => c.id !== "");
  const hasCases = cases.length > 0;

  const handleDelete = () => {
    if (!confirm(`Delete workflow "${workflow.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(workflow.id, {
      onSuccess: () => {
        void navigate({ to: "/workflows" });
      },
    });
  };

  const mutationError = deleteMutation.error;

  return (
    <Card accent="danger">
      <Stack gap="md">
        <h3>Danger zone</h3>
        {hasCases ? (
          <>
            <p>
              This workflow has {cases.length} case
              {cases.length === 1 ? "" : "s"} referencing it. Delete or
              close out the case
              {cases.length === 1 ? "" : "s"} before deleting the workflow:
            </p>
            <ul>
              {cases.map((c) => (
                <li key={c.id}>
                  <Link to="/cases/$id" params={{ id: c.id }}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div>
              <Button type="button" variant="danger" disabled>
                Delete workflow
              </Button>
            </div>
          </>
        ) : (
          <>
            <p>
              Delete this workflow. The persisted definition is removed
              and the workflow can no longer be used to create new cases.
            </p>
            <div>
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete workflow"}
              </Button>
            </div>
            {mutationError !== null && (
              <Alert variant="danger" icon={<AlertTriangle size={18} />}>
                {mutationError instanceof HalError && mutationError.status === 409
                  ? "Can't delete this workflow — a case was created while you weren't looking. Refresh the page and try again."
                  : "Something went wrong deleting the workflow. Please try again."}
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}
